// These are the element tags Lumina can render inside a <lumina>…</lumina> block. They mirror the
// ElementTag union in @atlassian/lumina (src/layout/element/types.ts), which we can't import
// directly since it isn't on a public subpath — so if Lumina adds or renames a tag, add it here too.
const KNOWN_LUMINA_ELEMENT_TAGS = [
	'action-card',
	'carousel',
	'md',
	'people-entity',
	'people-entity-list',
	'project-highlights',
	'related-people',
	'smart-link',
	'smart-link-list',
] as const;

const TAG_MATCHERS: ReadonlyArray<readonly [string, RegExp]> = KNOWN_LUMINA_ELEMENT_TAGS.map(
	(tag) => [tag, new RegExp(`<${tag}(?=[\\s/>]|$)`)] as const,
);

/**
 * Tells you which Lumina element types show up across a set of raw <lumina> blocks.
 *
 * We only care which element types are present for analytics, so this just scans for the tag names
 * rather than running the full Lumina parser. The result is a controlled set of tag strings — no
 * URLs, titles, or user content — so it's safe to send in an event.
 *
 * @param luminaBlockContents - raw <lumina> block strings (e.g. Object.values(luminaContentMap))
 * @returns sorted, de-duplicated tag list, e.g. ['smart-link', 'smart-link-list']
 */
export const getLuminaContentTypes = (luminaBlockContents: string[]): string[] => {
	const found = new Set<string>();

	for (const content of luminaBlockContents) {
		if (!content) {
			continue;
		}
		for (const [tag, matcher] of TAG_MATCHERS) {
			if (matcher.test(content)) {
				found.add(tag);
			}
		}
	}

	return Array.from(found).sort();
};
